"use client";
/**
 * SessionSidebar — conversation list with filter, rename, star, archive, delete.
 * Track C2.
 *
 * Session switch uses window.location.href (full reload) to avoid mermaid DOM conflicts.
 */
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Search,
  Star,
  StarOff,
  Archive,
  ArchiveRestore,
  MoreHorizontal,
  Pencil,
  Trash2,
  PanelLeftClose,
  PanelLeft,
  ChevronLeft,
  CornerDownLeft,
  X,
} from "lucide-react";
import type { Session } from "@/modes/ask/types";
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";
import {
  buildSessionDocs,
  searchSessions,
  snippetForResult,
  highlightLabel,
  type SessionSearchResult,
  type SnippetPart,
} from "@/modes/ask/lib/sessionSearch";

type FilterTab = "all" | "starred" | "archived";

/** How many rows the type-ahead dropdown shows above its "see all" footer. */
const MAX_SUGGESTIONS = 6;

/** Keystroke debounce for the search box, in ms. */
const SEARCH_DEBOUNCE_MS = 150;

/** "Today / Yesterday / N days ago / date" for a conversation timestamp. */
function relativeDate(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)  return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

/** Render snippet parts with the matched term wrapped in <mark>. */
function Highlight({ parts, className }: { parts: SnippetPart[]; className?: string }) {
  return (
    <span className={className}>
      {parts.map((part, i) =>
        part.match ? (
          <mark key={i} className="bg-amber-400/30 text-inherit rounded-sm px-0.5">{part.text}</mark>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </span>
  );
}

interface SessionSidebarProps {
  sessions:         Session[];
  isLoading:        boolean;
  activeSessionId?: string;
  isCollapsed:      boolean;
  isMobile?:        boolean;
  onSelectSession:  (sessionId: string) => void;
  onNewChat:        () => void;
  onToggleCollapse: () => void;
  onCloseMobile?:   () => void;
  onToggleStar:     (sessionId: string) => Promise<void>;
  onToggleArchive:  (sessionId: string) => Promise<void>;
  onRename:         (sessionId: string, newTitle: string) => Promise<void>;
  onDelete:         (sessionId: string) => Promise<void>;
  sessionsUsed?:    number;
  sessionsLimit?:   number;
  sessionLimitReached?: boolean;
}

export function SessionSidebar({
  sessions,
  isLoading,
  activeSessionId,
  isCollapsed,
  isMobile = false,
  onSelectSession,
  onNewChat,
  onToggleCollapse,
  onCloseMobile,
  onToggleStar,
  onToggleArchive,
  onRename,
  onDelete,
  sessionsUsed = 0,
  sessionsLimit = 5,
  sessionLimitReached = false,
}: SessionSidebarProps) {
  const [filter,        setFilter]        = useState<FilterTab>("all");
  const [search,        setSearch]        = useState("");
  const [openMenu,      setOpenMenu]      = useState<string | null>(null);
  const [renameId,      setRenameId]      = useState<string | null>(null);
  const [renameValue,   setRenameValue]   = useState("");
  const [deleteId,      setDeleteId]      = useState<string | null>(null);
  const menuRef                           = useRef<HTMLDivElement>(null);

  // ── Search ────────────────────────────────────────────────────────────────
  // Gmail-style: type to get ranked suggestions in a dropdown, Enter (or the
  // "see all" row) to commit the query and see the full result list.

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [suggestOpen,      setSuggestOpen]      = useState(false);
  const [highlightIndex,   setHighlightIndex]   = useState(0);
  const searchRef                             = useRef<HTMLInputElement>(null);
  const suggestRef                            = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // The expensive part — indexing every message of every session — only
  // re-runs when the conversation list itself changes.
  const searchDocs = useMemo(() => buildSessionDocs(sessions), [sessions]);

  // Matches for the CURRENT (un-debounced) term power the dropdown, so the
  // suggestions feel instant; the list follows the debounced term.
  const ranked = useMemo(
    () => searchSessions(searchDocs, search),
    [searchDocs, search]
  );
  const suggestions = useMemo(() => ranked.slice(0, MAX_SUGGESTIONS), [ranked]);
  const allResults = useMemo(
    () => searchSessions(searchDocs, debouncedSearch),
    [searchDocs, debouncedSearch]
  );

  const trimmedSearch = debouncedSearch.trim();
  const isSearching   = trimmedSearch.length > 0;

  // Click-away closes the dropdown but keeps the committed query visible.
  useEffect(() => {
    if (!suggestOpen) return;
    const handler = (e: MouseEvent) => {
      const inInput = searchRef.current?.contains(e.target as Node);
      const inPanel  = suggestRef.current?.contains(e.target as Node);
      if (!inInput && !inPanel) setSuggestOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [suggestOpen]);

  // Opening a conversation carries the query over, so the in-chat search can
  // auto-jump to the first hit inside that conversation.
  //
  // NOTE: the param is `find`, NOT `q` — `?q=` is the homepage hero-search
  // contract (fills the composer and auto-submits a new question), so reusing
  // it here destroyed the session id and bounced the user to /login.
  const openResult = useCallback((result: SessionSearchResult) => {
    const q = search.trim();
    window.location.href = q
      ? `/ask/${result.session.id}?find=${encodeURIComponent(q)}`
      : `/ask/${result.session.id}`;
  }, [search]);

  const commitSearch = useCallback(() => {
    setSuggestOpen(false);
    setHighlightIndex(0);
  }, []);

  // Close the dropdown when the user leaves the sidebar entirely.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && suggestOpen) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [suggestOpen]);

  // Close dropdown → menu helper
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Filtered list ─────────────────────────────────────────────────────────

  // Rows to render: full-text hits when searching, otherwise the plain
  // recency-ordered list narrowed by the active tab.
  const rows = useMemo((): Array<{ session: Session; result: SessionSearchResult | null }> => {
    const matchesTab = (s: Session) => {
      if (filter === "starred")  return !!s.isStarred;
      if (filter === "archived") return !!s.isArchived;
      return !s.isArchived;
    };
    if (isSearching) {
      return allResults
        .filter((r) => matchesTab(r.session))
        .map((r) => ({ session: r.session, result: r }));
    }
    return sessions
      .filter(matchesTab)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .map((session) => ({ session, result: null }));
  }, [isSearching, allResults, sessions, filter]);

  // ── Token progress ────────────────────────────────────────────────────────

  const tokenPct = (used: number, limit: number) =>
    Math.min((used / limit) * 100, 100);

  // ── Rename submit ─────────────────────────────────────────────────────────

  const handleRenameSubmit = async () => {
    if (renameId && renameValue.trim()) {
      await onRename(renameId, renameValue.trim());
    }
    setRenameId(null);
    setRenameValue("");
  };

  // ── Collapsed view ────────────────────────────────────────────────────────

  if (isCollapsed && !isMobile) {
    return (
      <div className="w-16 bg-card border-r border-border flex flex-col items-center py-4 gap-4 h-full">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Expand sidebar"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
        <button
          onClick={onNewChat}
          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="New chat"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  }

  // ── Full view ─────────────────────────────────────────────────────────────

  return (
    <div
      className={`bg-card border-r border-border flex flex-col ${
        isMobile ? "w-72 h-[calc(100vh-4rem)]" : "w-72 h-full"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Chats</h2>
            {sessionLimitReached && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">
                {sessionsUsed}/{sessionsLimit}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onNewChat}
              className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
              aria-label={sessionLimitReached ? "Session limit reached" : "New chat"}
            >
              <Plus className="h-4 w-4" />
            </button>
            {isMobile ? (
              <button
                onClick={onCloseMobile}
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={onToggleCollapse}
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Search — Gmail-style type-ahead over titles, messages and files */}
        <div className="relative mb-3" ref={suggestRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setHighlightIndex(0); setSuggestOpen(true); }}
            onFocus={() => setSuggestOpen(true)}
            onKeyDown={(e) => {
              // +1 row for the "see all results" footer.
              const lastIndex = suggestions.length;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSuggestOpen(true);
                setHighlightIndex((i) => (i >= lastIndex ? 0 : i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => (i <= 0 ? lastIndex : i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                // Enter only picks a row while the dropdown is showing;
                // otherwise it commits the query and shows every result.
                const picked = suggestOpen ? suggestions[highlightIndex] : undefined;
                if (picked) openResult(picked);
                else commitSearch();
              } else if (e.key === "Escape") {
                if (suggestOpen) { e.preventDefault(); setSuggestOpen(false); }
                else if (search) { setSearch(""); }
              }
            }}
            placeholder="Search all chats..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-indigo-500/50"
            aria-label="Search all conversations"
            role="combobox"
            aria-expanded={suggestOpen && search.trim().length > 0}
            aria-controls="session-search-suggestions"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); searchRef.current?.focus(); }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Suggestions dropdown */}
          {suggestOpen && search.trim().length > 0 && (
            <div
              id="session-search-suggestions"
              role="listbox"
              className="absolute left-0 right-0 top-full mt-1 z-50 max-h-80 overflow-y-auto rounded-xl border border-border bg-background shadow-xl py-1
                         [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full"
            >
              {suggestions.length === 0 ? (
                <p className="px-3 py-3 text-xs text-muted-foreground">No matches</p>
              ) : (
                <>
                  {suggestions.map((r, i) => (
                    <button
                      key={r.session.id}
                      type="button"
                      role="option"
                      aria-selected={i === highlightIndex}
                      onMouseEnter={() => setHighlightIndex(i)}
                      onClick={() => openResult(r)}
                      className={`w-full text-left px-3 py-2 transition-colors ${
                        i === highlightIndex ? "bg-indigo-500/10" : "hover:bg-muted/60"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium truncate text-foreground">
                          <Highlight parts={highlightLabel(r.session.title, search)} />
                        </span>
                        {r.session.isStarred && (
                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                        <span className="ml-auto text-[10px] text-muted-foreground flex-shrink-0">
                          {relativeDate(r.session.updatedAt)}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        <Highlight parts={snippetForResult(r, search)} />
                      </p>
                    </button>
                  ))}

                  <div className="my-1 h-px bg-border" />
                  <button
                    type="button"
                    onClick={commitSearch}
                    onMouseEnter={() => setHighlightIndex(suggestions.length)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                      highlightIndex === suggestions.length
                        ? "bg-indigo-500/10 text-foreground"
                        : "text-muted-foreground hover:bg-muted/60"
                    }`}
                  >
                    <Search className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">
                      See all results for &ldquo;{search.trim()}&rdquo;
                    </span>
                    <span className="ml-auto text-[10px] font-mono flex-shrink-0">
                      {ranked.length}
                    </span>
                    <CornerDownLeft className="h-3 w-3 flex-shrink-0" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1">
          {(["all", "starred", "archived"] as FilterTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`flex-1 flex items-center justify-center gap-1 h-8 rounded-lg text-xs font-medium transition-colors capitalize ${
                filter === tab
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {tab === "starred" && <Star className="h-3 w-3" />}
              {tab === "archived" && <Archive className="h-3 w-3" />}
              <span className={tab !== "all" ? "hidden sm:inline" : ""}>{tab}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <SenseiMarkLoader size={32} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10 px-4">
            {isSearching
              ? "No matches found"
              : filter === "starred"
              ? "No starred conversations"
              : filter === "archived"
              ? "No archived conversations"
              : "No conversations yet"}
          </p>
        ) : (
          rows.map(({ session, result }) => {
            const pct     = tokenPct(session.tokensUsed, session.tokensLimit);
            const isOver  = session.tokensUsed > session.tokensLimit;
            const isActive = session.id === activeSessionId;

            return (
              <div
                key={session.id}
                className={`group mx-2 mb-1 px-3 py-3 rounded-lg cursor-pointer transition-colors relative ${
                  isActive ? "bg-muted" : "hover:bg-muted/50"
                }`}
                onClick={() => {
                  // Full reload to avoid mermaid DOM conflicts
                  const q = trimmedSearch;
                  window.location.href = q
                    ? `/ask/${session.id}?find=${encodeURIComponent(q)}`
                    : `/ask/${session.id}`;
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium truncate">
                        {isSearching
                          ? <Highlight parts={highlightLabel(session.title, trimmedSearch)} />
                          : session.title}
                      </span>
                      {session.isStarred && (
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {result
                        ? <Highlight parts={snippetForResult(result, trimmedSearch)} />
                        : session.lastMessage}
                    </p>
                    {result && (
                      <p className="text-[10px] text-indigo-400/80 mt-1">
                        {result.totalCount} {result.totalCount === 1 ? "match" : "matches"} in this chat
                      </p>
                    )}

                    {/* Token progress */}
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>{session.tokensUsed.toLocaleString()} / {session.tokensLimit.toLocaleString()}</span>
                        <span className={isOver ? "text-red-500" : ""}>{pct.toFixed(0)}%</span>
                      </div>
                      <div className="token-bar">
                        <div
                          className={`token-bar-fill ${isOver ? "token-bar-fill--over" : pct > 80 ? "token-bar-fill--warn" : ""}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>

                    <p className="text-[10px] text-muted-foreground/70 mt-1">
                      {relativeDate(session.updatedAt)}
                    </p>
                  </div>

                  {/* Kebab menu */}
                  <div ref={openMenu === session.id ? menuRef : null} className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenu(openMenu === session.id ? null : session.id);
                      }}
                      className={`h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all ${
                        isMobile
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                      aria-label="More actions"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>

                    {openMenu === session.id && (
                      <div
                        className="absolute right-0 top-8 z-50 w-48 bg-background border border-border/50 rounded-xl shadow-xl p-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            setRenameId(session.id);
                            setRenameValue(session.title);
                            setOpenMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors text-left"
                        >
                          <Pencil className="h-4 w-4" /> Rename
                        </button>
                        <button
                          onClick={() => { onToggleStar(session.id); setOpenMenu(null); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors text-left"
                        >
                          {session.isStarred ? (
                            <><StarOff className="h-4 w-4" /> Remove star</>
                          ) : (
                            <><Star className="h-4 w-4" /> Add star</>
                          )}
                        </button>
                        <button
                          onClick={() => { onToggleArchive(session.id); setOpenMenu(null); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors text-left"
                        >
                          {session.isArchived ? (
                            <><ArchiveRestore className="h-4 w-4" /> Unarchive</>
                          ) : (
                            <><Archive className="h-4 w-4" /> Archive</>
                          )}
                        </button>
                        <div className="my-1 h-px bg-border" />
                        <button
                          onClick={() => { setDeleteId(session.id); setOpenMenu(null); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-red-500/10 text-red-500 transition-colors text-left"
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Rename modal */}
      {renameId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div
            className="bg-background border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Rename Conversation</h3>
            <input
              autoFocus
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleRenameSubmit(); if (e.key === "Escape") setRenameId(null); }}
              className="w-full px-4 py-2.5 rounded-xl bg-muted border border-border text-sm outline-none focus:border-indigo-500/50 mb-4"
              placeholder="Conversation name"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRenameId(null)}
                className="px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameSubmit}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-pink-500 text-white text-sm font-medium"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div
            className="bg-background border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">Delete Conversation</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await onDelete(deleteId);
                  setDeleteId(null);
                }}
                className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
